"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Icons } from "@/components/icons";
import {
  WEBSITE_BUILDER_FEATURES,
  WORKFLOW_NODES,
  WORKFLOW_FEATURES,
  TEMPLATE_CARDS,
  TEMPLATE_FEATURES,
} from "../_data/page-data";
import {
  AnimatedSection,
  StaggerGrid,
  FadeInChild,
  GlowCard,
  FloatingElement,
} from "./motion";

/* ─── Mock IDE Window (for AI Website Builder card) ─── */
function MockIDE() {
  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/10 via-transparent to-violet-500/10 rounded-2xl blur-2xl" />
      <div className="relative rounded-2xl border border-slate-200/80 bg-[#1e1e1e] shadow-2xl overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#181818] border-b border-[#2d2d2d]">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
            <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
            <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          </div>
          <span className="text-[11px] text-slate-500 ml-2">AI Web Builder</span>
        </div>
        <div className="flex h-64">
          {/* Chat panel */}
          <div className="w-1/3 border-r border-[#2d2d2d] p-3 space-y-2">
            <div className="text-[9px] text-slate-600 uppercase tracking-wider mb-3">AI Chat</div>
            <div className="rounded-lg bg-indigo-600 px-2.5 py-1.5 text-[10px] text-white ml-auto w-fit">
              Build a portfolio with dark theme
            </div>
            <div className="rounded-lg bg-[#2a2d2e] px-2.5 py-1.5 text-[10px] text-slate-400 w-fit">
              Generated 5 files with React + Tailwind...
            </div>
          </div>
          {/* Code panel */}
          <div className="w-1/3 border-r border-[#2d2d2d] p-3">
            <div className="flex gap-2 mb-2">
              <span className="text-[9px] text-blue-400 bg-[#1e1e1e] px-2 py-0.5 rounded border-t-2 border-indigo-500">
                App.tsx
              </span>
              <span className="text-[9px] text-slate-600 px-2 py-0.5">index.css</span>
            </div>
            <div className="space-y-1 font-mono text-[8px] text-slate-500">
              <div>
                <span className="text-purple-400">export</span>{" "}
                <span className="text-blue-400">function</span>{" "}
                <span className="text-yellow-300">App</span>() {"{"}
              </div>
              <div className="pl-3">
                <span className="text-purple-400">return</span> (
              </div>
              <div className="pl-5">
                <span className="text-slate-400">&lt;</span>
                <span className="text-emerald-400">div</span>
                <span className="text-slate-400">&gt;</span>
              </div>
              <div className="pl-7 text-slate-600">...</div>
            </div>
          </div>
          {/* Preview panel */}
          <div className="w-1/3 bg-white p-3 flex items-center justify-center">
            <div className="text-center space-y-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 mx-auto" />
              <div className="h-1.5 w-16 bg-slate-200 rounded mx-auto" />
              <div className="h-1 w-12 bg-slate-100 rounded mx-auto" />
              <div className="grid grid-cols-2 gap-1 mt-2">
                <div className="h-8 bg-slate-100 rounded" />
                <div className="h-8 bg-slate-100 rounded" />
              </div>
            </div>
          </div>
        </div>
        <div className="h-5 bg-indigo-600 px-3 flex items-center">
          <span className="text-[8px] text-white/70">AI Web Builder · React + Tailwind</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Checkmark list helper ─── */
function CheckList({
  items,
  color = "text-indigo-500",
  cols = 2,
}: {
  items: readonly string[];
  color?: string;
  cols?: number;
}) {
  return (
    <ul className={`grid ${cols === 2 ? "sm:grid-cols-2" : "grid-cols-2"} gap-3 text-sm`}>
      {items.map((f) => (
        <li key={f} className="flex items-center gap-2">
          <Icons.check className={`h-4 w-4 shrink-0 ${color}`} />
          <span className="text-slate-600">{f}</span>
        </li>
      ))}
    </ul>
  );
}

/* ─── Workflow mini diagram ─── */
function WorkflowDiagram() {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200/80 p-4">
      <div className="flex items-center justify-between gap-2">
        {WORKFLOW_NODES.map((n, i) => (
          <div key={n.label} className="flex items-center gap-2">
            <div className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-semibold ${n.color}`}>
              {n.label}
            </div>
            {i < WORKFLOW_NODES.length - 1 && (
              <svg className="h-3 w-5 text-slate-300" fill="none" viewBox="0 0 20 12">
                <path
                  d="M1 6h16m0 0l-4-4m4 4l-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   What's New Section
   ═══════════════════════════════════════════════ */

export function WhatsNewSection() {
  return (
    <AnimatedSection id="whats-new" className="container py-16 md:py-24 lg:py-32">
      <div className="mx-auto max-w-6xl">
        {/* Section header */}
        <div className="flex flex-col items-center space-y-4 text-center mb-16">
          <span className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200">
            What&apos;s New
          </span>
          <h2 className="font-heading text-3xl leading-[1.1] sm:text-3xl md:text-6xl">
            Ship faster with <span className="text-gradient">AI superpowers</span>
          </h2>
          <p className="max-w-[600px] leading-relaxed text-muted-foreground sm:text-lg">
            Three powerful new tools that turn ideas into production-ready products — no code required.
          </p>
        </div>

        {/* AI Website Builder — Hero Card */}
        <AnimatedSection
          direction="left"
          className="relative mb-8 overflow-hidden rounded-3xl border border-slate-200/60 bg-gradient-to-br from-white via-indigo-50/30 to-violet-50/50 shadow-xl shadow-indigo-500/5"
        >
          <div className="absolute inset-0 bg-grid-slate-100/50 [mask-image:linear-gradient(0deg,transparent,black)] pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-8 p-8 md:p-12">
            <div className="flex flex-col justify-center space-y-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/25">
                  <Icons.webBuilder className="h-5 w-5 text-white" />
                </div>
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-[11px] font-semibold text-indigo-700 animate-pulse-glow">
                  NEW
                </span>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                AI Website Builder
              </h3>
              <p className="text-base leading-relaxed text-muted-foreground">
                Describe your website in plain English and watch it come to life. Full React/HTML
                generation with live preview, powered by your own API key. Edit code in a VS
                Code-style editor or iterate via chat — like v0 and Lovable, built right in.
              </p>
              <CheckList items={WEBSITE_BUILDER_FEATURES} />
              <div>
                <Link
                  href="/login"
                  className={cn(
                    buttonVariants({ size: "lg" }),
                    "rounded-full px-8 shadow-lg shadow-indigo-500/20 transition-all hover:shadow-xl hover:shadow-indigo-500/30 hover:-translate-y-0.5"
                  )}
                >
                  <Icons.webBuilder className="h-4 w-4 mr-2" />
                  Try Website Builder
                </Link>
              </div>
            </div>

            <FloatingElement distance={8} duration={8}>
              <MockIDE />
            </FloatingElement>
          </div>
        </AnimatedSection>

        {/* Two-col: Workflow Builder + Template Marketplace */}
        <StaggerGrid className="grid gap-8 md:grid-cols-2">
          {/* Visual Workflow Builder */}
          <FadeInChild>
            <GlowCard glowColor="emerald">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative p-8 space-y-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/25">
                    <Icons.workflow className="h-5 w-5 text-white" />
                  </div>
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold text-emerald-700">
                    NEW
                  </span>
                </div>
                <h3 className="text-xl font-bold tracking-tight text-slate-900">
                  Visual Workflow Builder
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Design complex AI agent workflows with a drag-and-drop canvas. Connect triggers, AI
                  nodes, logic branches and actions — no code required.
                </p>
                <WorkflowDiagram />
                <CheckList items={WORKFLOW_FEATURES} color="text-emerald-500" cols={2} />
              </div>
            </GlowCard>
          </FadeInChild>

          {/* Template Marketplace */}
          <FadeInChild>
            <GlowCard glowColor="pink">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 via-transparent to-rose-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative p-8 space-y-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 shadow-lg shadow-pink-500/25">
                    <Icons.sparkles className="h-5 w-5 text-white" />
                  </div>
                  <span className="inline-flex items-center rounded-full bg-pink-100 px-3 py-1 text-[11px] font-semibold text-pink-700">
                    NEW
                  </span>
                </div>
                <h3 className="text-xl font-bold tracking-tight text-slate-900">
                  Template Marketplace
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Browse pre-built chatbot templates for customer support, e-commerce, SaaS
                  onboarding, HR and more. One-click import with full customization.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {TEMPLATE_CARDS.map((t) => (
                    <div
                      key={t.name}
                      className="rounded-lg border border-slate-200/80 bg-white p-3 text-center transition-transform hover:scale-105"
                    >
                      <div
                        className={`mx-auto mb-2 h-8 w-8 rounded-lg bg-gradient-to-br ${t.color} flex items-center justify-center`}
                      >
                        <Icons.bot className="h-3.5 w-3.5 text-white" />
                      </div>
                      <p className="text-[10px] font-medium text-slate-700 leading-tight">
                        {t.name}
                      </p>
                    </div>
                  ))}
                </div>
                <CheckList items={TEMPLATE_FEATURES} color="text-pink-500" cols={2} />
              </div>
            </GlowCard>
          </FadeInChild>
        </StaggerGrid>
      </div>
    </AnimatedSection>
  );
}
